import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CalendarX, CheckCircle2, AlertCircle } from "lucide-react";
import { api } from "../lib/api";
import { Section, Overline } from "../components/Layout";
import Orbs from "../components/Orbs";

/**
 * Self-service cancellation, reached from the link in a confirmation email
 * (ND-5 / FR-REG-9). The token in the URL is booking-scoped and long-lived, so
 * this works days later when the 60-minute verified session is long gone.
 *
 * Deliberately a two-step confirm: the link is in an email, so it can be clicked
 * by accident or by a mail client prefetching links. Nothing is cancelled until
 * the button is pressed.
 */
export default function CancelBooking() {
  const { token } = useParams();
  const [state, setState] = useState({ phase: "loading" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.post("/cancel/preview", { token })
      .then((r) => setState({ phase: "confirm", ...r.data }))
      .catch((e) => setState({
        phase: "error",
        message: e.response?.data?.detail || "This cancellation link is invalid or has expired.",
      }));
  }, [token]);

  const cancel = async () => {
    setBusy(true);
    try {
      // One endpoint: the token already names what it cancels.
      const r = await api.post("/cancel", { token });
      setState((s) => ({ ...s, phase: "done", promoted: r.data?.promoted }));
    } catch (e) {
      setState((s) => ({
        ...s, phase: "error",
        message: e.response?.data?.detail || "We couldn't cancel that. Please contact us.",
      }));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen relative">
      <Orbs />
      <Section className="pt-24 pb-16" innerClassName="max-w-lg mx-auto">
        <div className="glass-panel p-8 text-center" data-testid="cancel-panel">
          {state.phase === "loading" && (
            <div className="text-sm text-muted-foreground">Checking your booking…</div>
          )}

          {state.phase === "error" && (
            <>
              <div className="h-12 w-12 rounded-full mx-auto flex items-center justify-center"
                   style={{ background: "hsl(var(--vd-coral) / 0.14)", color: "hsl(var(--vd-coral))" }}>
                <AlertCircle size={18} />
              </div>
              <div className="mt-3 font-heading text-xl">Can't cancel this</div>
              <div className="text-sm text-muted-foreground mt-1" data-testid="cancel-error">{state.message}</div>
            </>
          )}

          {state.phase === "confirm" && (
            <>
              <div className="h-12 w-12 rounded-full mx-auto flex items-center justify-center"
                   style={{ background: "hsl(var(--vd-peach) / 0.18)", color: "hsl(var(--vd-peach-ink))" }}>
                <CalendarX size={18} />
              </div>
              <Overline className="mt-4">Cancel your spot</Overline>
              <div className="mt-1 font-heading text-2xl leading-tight">{state.title}</div>
              {state.when && <div className="text-sm text-muted-foreground mt-1 font-mono">{state.when}</div>}
              <div className="text-xs text-muted-foreground mt-3">{state.email}</div>

              {state.status === "cancelled" || state.status === "available" ? (
                <div className="mt-6 text-sm text-muted-foreground">This is already cancelled.</div>
              ) : (
                <>
                  <button onClick={cancel} disabled={busy} className="btn-primary w-full mt-6"
                          data-testid="cancel-confirm">
                    {busy ? "Cancelling…" : "Yes, cancel my spot"}
                  </button>
                  <div className="text-xs text-muted-foreground mt-3">
                    Cancelling frees your place for someone else. You can register again if plans change.
                  </div>
                </>
              )}
            </>
          )}

          {state.phase === "done" && (
            <>
              <div className="h-12 w-12 rounded-full mx-auto flex items-center justify-center"
                   style={{ background: "hsl(var(--vd-green) / 0.14)", color: "hsl(var(--vd-green))" }}>
                <CheckCircle2 size={18} />
              </div>
              <div className="mt-3 font-heading text-xl">You're cancelled</div>
              <div className="text-sm text-muted-foreground mt-1" data-testid="cancel-done">
                Thanks for letting us know — we've freed your place.
                {state.promoted && " Someone on the waitlist has been given your spot."}
              </div>
              <a href="/events" className="btn-secondary inline-flex mt-6">Browse other events</a>
            </>
          )}
        </div>
      </Section>
    </div>
  );
}
